import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ScanLine, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

const HINTS = new Map([[DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128]]]);
let READER = null;
const getReader = () => (READER ||= new BrowserMultiFormatReader(HINTS, { delayBetweenScanAttempts: 80, delayBetweenScanSuccess: 600 }));

// Single shared camera component — continuous EAN/UPC decode (scan) or shutter (photo).
// Full-bleed feed, no bounding box. Reused by the main scanner AND the manual-entry form.
export function ScannerCamera({ open, mode = "scan", onDetected, onCapture, onClose, demoProducts = [] }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const controlsRef = useRef(null);
  const lastCodeRef = useRef(null);
  const [torchOn, setTorchOn] = useState(false);

  const stopCam = () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  const stopScan = () => { try { controlsRef.current?.stop(); } catch { /* noop */ } controlsRef.current = null; };
  const stopAll = () => { stopScan(); stopCam(); setTorchOn(false); };

  const startScan = () => {
    if (!videoRef.current) return;
    try {
      const reader = getReader();
      reader.decodeFromVideoElement(videoRef.current, (result) => {
        if (result) {
          const code = result.getText();
          if (code && code !== lastCodeRef.current) { lastCodeRef.current = code; onDetected?.(code); }
        }
      }).then((c) => { controlsRef.current = c; }).catch(() => { /* decode unavailable — demo scan fallback */ });
    } catch { /* zxing unavailable */ }
  };

  useEffect(() => {
    if (!open) { stopAll(); return; }
    lastCodeRef.current = null;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        if (mode === "scan") startScan();
        try { const track = stream.getVideoTracks()[0]; const caps = track.getCapabilities ? track.getCapabilities() : {}; if (caps.focusMode && caps.focusMode.includes("continuous")) await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }); } catch { /* focus not supported */ }
      } catch { toast.info("Camera unavailable — use the demo scan"); }
    })();
    return () => { cancelled = true; stopAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const toggleTorch = async () => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      const caps = track?.getCapabilities ? track.getCapabilities() : {};
      if (!caps.torch) { toast.info("Flash not supported on this device"); return; }
      const next = !torchOn; await track.applyConstraints({ advanced: [{ torch: next }] }); setTorchOn(next);
    } catch { toast.info("Flash unavailable"); }
  };

  const capture = () => {
    let thumb = null;
    try { const v = videoRef.current; if (v && v.videoWidth) { const c = document.createElement("canvas"); c.width = v.videoWidth; c.height = v.videoHeight; c.getContext("2d").drawImage(v, 0, 0, c.width, c.height); thumb = c.toDataURL("image/jpeg", 0.9); } } catch { /* ignore */ }
    onCapture?.(thumb);
  };
  const demo = () => {
    const p = demoProducts.length ? demoProducts[Math.floor(Math.random() * demoProducts.length)] : null;
    const code = p ? (p.barcode || p.name) : String(Math.floor(5000000000000 + Math.random() * 9999999999));
    onDetected?.(code);
  };

  if (!open) return null;
  return createPortal(
    <div data-testid="camera-modal" className="fixed inset-0 z-[70] bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 bg-black/60 text-white shrink-0" style={{ paddingTop: "calc(env(safe-area-inset-top) + 12px)" }}>
        <span className="font-bold text-sm flex items-center gap-2"><ScanLine className="w-4 h-4 text-emerald-400" /> {mode === "photo" ? "Photo capture" : "Scan Barcode"}</span>
        <button data-testid="camera-close-btn" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-90"><X className="w-5 h-5" /></button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
        <button data-testid="camera-torch-btn" onClick={toggleTorch} className={`absolute top-3 right-3 w-11 h-11 rounded-full flex items-center justify-center active:scale-90 ${torchOn ? "bg-amber-400 text-black" : "bg-white/15 text-white"}`}><Zap className="w-5 h-5" /></button>
      </div>
      <div className="bg-black px-4 pt-4 flex flex-col items-center gap-3 shrink-0" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)" }}>
        {mode === "photo" ? (
          <button data-testid="camera-capture-btn" onClick={capture} aria-label="Take photo"
            className="w-[74px] h-[74px] rounded-full bg-white active:scale-90 transition-transform flex items-center justify-center ring-4 ring-white/30">
            <span className="w-[58px] h-[58px] rounded-full border-[3px] border-slate-900/80" />
          </button>
        ) : (
          <div data-testid="scan-hint" className="text-white/85 text-sm font-semibold flex items-center gap-2"><ScanLine className="w-4 h-4 text-emerald-400 animate-pulse" /> Point at a barcode — scanning automatically…</div>
        )}
        <button data-testid="camera-simulate-btn" onClick={demo} className="text-xs text-white/70 underline underline-offset-2">Use demo scan</button>
      </div>
    </div>,
    document.body
  );
}
